import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import "./landing.css";
import LoginModal from "../components/LoginModal";
import RegisterModal from "../components/RegisterModal";
import SEO from "../components/SEO";
import API from "../utils/API";

const LANDING_JSON_LD = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "'elloGovna",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    description:
        "'elloGovna is a government relations platform for California's Capitol community: track legislation in real time, search a directory of legislators and agency staff, build your professional network, follow curated policy news, and manage stakeholder outreach.",
    url: "https://elloGovna.com",
    offers: {
        "@type": "Offer",
        price: "14.99",
        priceCurrency: "USD",
    },
    publisher: {
        "@type": "Organization",
        name: "'elloGovna",
        url: "https://elloGovna.com",
        logo: "https://elloGovna.com/eG-logo512.png",
    },
};

const fmtShortDate = (iso) => {
    if (!iso) return null;
    return new Date(iso.slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', {
        month: 'short', day: 'numeric',
    });
};

const OVERVIEW_SLIDES = [
    {
        icon: "bi-bank",
        title: "Track Legislation in Real Time",
        desc: "Follow bills, amendments, and status changes the moment they happen in the Legislature.",
    },
    {
        icon: "bi-file-earmark-text",
        title: "Every Bill's Full Story",
        desc: "Author info, an AI-assisted summary, and the complete history of a bill in one profile.",
    },
    {
        icon: "bi-person-vcard",
        title: "Find the Right Person, Fast",
        desc: "A searchable directory of legislators, committee staff, and agency personnel.",
    },
    {
        icon: "bi-kanban",
        title: "One Dashboard for Your Team",
        desc: "Tasks, reports, and activity across your whole Workspace, all in one view.",
    },
];

const FEATURES = [
    {
        icon: "bi-bank",
        title: "Track Legislation",
        desc: "Monitor bills, amendments, and regulatory changes in real time. Never miss a move on the issues that matter to your clients.",
    },
    {
        icon: "bi-person-vcard",
        title: "Staff Directory",
        desc: "Search a comprehensive directory of legislators, committee staff, and agency personnel across the State of California.",
    },
    {
        icon: "bi-people-fill",
        title: "Build Your Network",
        desc: "Connect with lobbyists, advocates, and stakeholders. Find the right allies to advance your priorities.",
    },
    {
        icon: "bi-newspaper",
        title: "Stay Informed",
        desc: "Scroll curated headlines and policy news each morning so you always walk into the building ready.",
    },
    {
        icon: "bi-megaphone",
        title: "Post Announcements",
        desc: "Share press events, fundraisers, and office news with the entire Sacramento policy community in one place.",
    },
    {
        icon: "bi-briefcase",
        title: "Job Openings",
        desc: "Legislators and agencies can post staffing needs directly to the professionals who are already plugged in.",
    },
];

export default function Landing() {
    const [awardsMode, setAwardsMode] = useState(null);
    const [subscriptionsEnabled, setSubscriptionsEnabled] = useState(false);
    const [overviewSlide, setOverviewSlide] = useState(0);

    useEffect(() => {
        const id = setInterval(() => {
            setOverviewSlide((i) => (i + 1) % OVERVIEW_SLIDES.length);
        }, 5500);
        return () => clearInterval(id);
    }, []);

    useEffect(() => {
        API.getAppSettings()
            .then((res) => setSubscriptionsEnabled(!!res.data?.subscriptions_enabled))
            .catch(() => {});
    }, []);

    useEffect(() => {
        API.getAwardSettings()
            .then((res) => {
                const d = res.data;
                const today = new Date();
                const isOpen = (enabled, start, end) =>
                    enabled &&
                    (!start || new Date(start.slice(0, 10) + 'T00:00:00') <= today) &&
                    (!end   || new Date(end.slice(0, 10)   + 'T23:59:59') >= today);

                if (isOpen(d.voting_enabled, d.voting_start, d.voting_end)) {
                    setAwardsMode({
                        mode: 'voting',
                        roundName: d.round_name || null,
                        endDate: fmtShortDate(d.voting_end),
                    });
                } else if (isOpen(d.nominations_enabled, d.nominations_start, d.nominations_end)) {
                    setAwardsMode({
                        mode: 'nominations',
                        roundName: d.round_name || null,
                        endDate: fmtShortDate(d.nominations_end),
                    });
                }
            })
            .catch(() => {});
    }, []);

    const isVoting = awardsMode?.mode === 'voting';

    return (
        <>
            <SEO
                title="'elloGovna | Say 'ello to Government Relations"
                description="Track California legislation in real time, search a directory of legislators and agency staff, build your policy network, and stay on top of Capitol news — all from one dashboard."
                path="/"
                jsonLd={LANDING_JSON_LD}
            />
            {/* ── Hero ─────────────────────────────────────────────── */}
            <section className="eg-hero">
                <div className="eg-hero-overlay" />

                {awardsMode && (
                    <div className="eg-awards-banner">
                        <i className="bi bi-trophy-fill eg-awards-trophy mx-3" />
                        <div className="eg-awards-text">
                            {awardsMode.roundName && (
                                <span className="eg-awards-round">{awardsMode.roundName}</span>
                            )}
                            <span className="eg-awards-headline">
                                {isVoting ? 'Voting is Now Open!' : 'Nominations are Open!'}
                            </span>
                            <span className="eg-awards-sub">
                                {isVoting
                                    ? 'Cast your vote for a standout colleague'
                                    : 'Nominate a colleague for their exceptional work'}
                                {awardsMode.endDate ? ` — through ${awardsMode.endDate}.` : '.'}
                            </span>
                        </div>
                        <div className="eg-awards-banner-actions">
                            <button
                                className="eg-awards-cta"
                                data-bs-toggle="modal"
                                data-bs-target="#registerModal"
                            >
                                {isVoting ? 'Register to Vote' : 'Register to Nominate'}
                            </button>
                            <button
                                className="eg-awards-cta eg-awards-cta--ghost"
                                data-bs-toggle="modal"
                                data-bs-target="#loginModal"
                            >
                                Sign In
                            </button>
                        </div>
                    </div>
                )}

                <div className="eg-hero-content">
                    <p className="eg-hero-eyebrow">Discover &bull; Network &bull; Advocate</p>
                    <h1 className="eg-hero-brand">
                        <i className="eg-ello">'ello</i>Govna
                    </h1>
                    <p className="eg-hero-tagline">
                        The platform for government relations professionals.
                    </p>
                    <div className="eg-hero-actions">
                        <button
                            className="eg-btn eg-btn-primary"
                            data-bs-toggle="modal"
                            data-bs-target="#registerModal"
                        >
                            Get Started
                        </button>
                        <button
                            className="eg-btn eg-btn-ghost"
                            data-bs-toggle="modal"
                            data-bs-target="#loginModal"
                        >
                            Sign In
                        </button>
                    </div>
                </div>
            </section>

            {/* ── About ────────────────────────────────────────────── */}
            <section className="eg-about">
                <div className="eg-container">
                    <p className="eg-about-text">
                        Whether you&rsquo;re a lobbyist, legislative staffer, board member, or public affairs
                        officer,{" "}
                        <strong>
                            <i>'ello</i>Govna
                        </strong>{" "}
                        gives you a single, trusted space to stay connected to colleagues, track bills, and
                        engage the Sacramento policy community.
                    </p>
                </div>
            </section>

            {/* ── Features ─────────────────────────────────────────── */}
            <section className="eg-features">
                <div className="eg-container">
                    <h2 className="eg-section-title">Everything you need. All in one place.</h2>
                    <div className="eg-feature-grid">
                        {FEATURES.map(({ icon, title, desc }) => (
                            <div className="eg-feature-card" key={title}>
                                <div className="eg-feature-icon">
                                    <i className={`bi ${icon}`} />
                                </div>
                                <h3 className="eg-feature-title">{title}</h3>
                                <p className="eg-feature-desc">{desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── Subscription teaser (shown only when subscriptions are enabled) ── */}
            {subscriptionsEnabled && (
                <section className="eg-subscribe-teaser">
                    <div className="eg-container">
                        <div className="eg-sub-teaser-inner">
                            <div className="eg-sub-teaser-text">
                                <p className="eg-sub-teaser-eyebrow">Premium Access</p>
                                <h2 className="eg-sub-teaser-title">
                                    Subscription plans for every policy professional
                                </h2>
                                <p className="eg-sub-teaser-body">
                                    Whether you track bills independently or manage a full government
                                    relations team, <i>'ello</i>Govna has a plan built for how you work.
                                    Get full access to legislation, bill profiles, staff directory, and
                                    team collaboration tools.
                                </p>
                                <Link to="/plans" className="eg-btn eg-btn-primary">
                                    View Plans &amp; Pricing
                                </Link>
                            </div>
                            <div className="eg-sub-teaser-cards">
                                <div className="eg-sub-plan-card">
                                    <div className="eg-sub-plan-icon">
                                        <i className="bi bi-person-check" />
                                    </div>
                                    <div>
                                        <p className="eg-sub-plan-name">Individual</p>
                                        <p className="eg-sub-plan-price">
                                            $14.99 <span>/month</span>
                                        </p>
                                        <p className="eg-sub-plan-desc">
                                            Dashboard, legislation tracking &amp; bill profiles
                                        </p>
                                    </div>
                                </div>
                                <div className="eg-sub-plan-card eg-sub-plan-card--workplace">
                                    <div className="eg-sub-plan-icon">
                                        <i className="bi bi-people" />
                                    </div>
                                    <div>
                                        <p className="eg-sub-plan-name">Workspace</p>
                                        <p className="eg-sub-plan-price">
                                            $74.99 <span>/month</span>
                                        </p>
                                        <p className="eg-sub-plan-desc">
                                            Everything in Individual plus shared team tools
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* ── Overview teaser ──────────────────────────────────── */}
            <section className="eg-overview-teaser">
                <div className="eg-container eg-overview-teaser-inner">
                    <div className="eg-overview-carousel">
                        <div className="eg-overview-carousel-track">
                            {OVERVIEW_SLIDES.map((slide, i) => (
                                <div
                                    key={slide.title}
                                    className={`eg-overview-carousel-slide ${i === overviewSlide ? 'is-active' : ''}`}
                                >
                                    <div className="eg-overview-carousel-icon">
                                        <i className={`bi ${slide.icon}`} />
                                    </div>
                                    <h3>{slide.title}</h3>
                                    <p>{slide.desc}</p>
                                </div>
                            ))}
                        </div>
                        <div className="eg-overview-carousel-dots">
                            {OVERVIEW_SLIDES.map((slide, i) => (
                                <button
                                    key={slide.title}
                                    type="button"
                                    className={`eg-overview-carousel-dot ${i === overviewSlide ? 'is-active' : ''}`}
                                    aria-label={`Show ${slide.title}`}
                                    onClick={() => setOverviewSlide(i)}
                                />
                            ))}
                        </div>
                    </div>

                    <div className="eg-overview-teaser-text">
                        <p className="eg-overview-teaser-eyebrow">See It In Action</p>
                        <h2 className="eg-overview-teaser-title">
                            Take a look inside <i>'ello</i>Govna
                        </h2>
                        <p className="eg-overview-teaser-body">
                            Walk through real examples of legislative tracking, bill profiles, the staff
                            directory, and more — no account required.
                        </p>
                        <Link to="/overview" className="eg-btn eg-btn-overview">
                            View the Overview
                        </Link>
                    </div>
                </div>
            </section>

            {/* ── CTA ──────────────────────────────────────────────── */}
            <section className="eg-cta pb-4">
                <div className="eg-container eg-cta-inner">
                    <h2 className="eg-cta-title">Ready to get to work?</h2>
                    <p className="eg-cta-body">
                        <span
                            className="eg-cta-link"
                            data-bs-toggle="modal"
                            data-bs-target="#registerModal"
                        >
                            Register today
                        </span>{" "}
                        to access the full platform. If you work for or do business with the State of
                        California, you&rsquo;re eligible. 
                        
                        <br/><br/><button
                        className="eg-btn eg-btn-primary"
                        data-bs-toggle="modal"
                        data-bs-target="#registerModal"
                    >
                        Create Your Account
                    </button>
                    <br/><br/><br/>  Not sure yet? <Link to="/overview">See an overview</Link> of what's inside.
                        Have questions? Check our{" "}
                        <Link to="/faq">FAQ</Link> or reach out to{" "}
                        <a href="mailto:info@ellogovna.com">info@ellogovna.com</a>.
                    </p>
                    
                </div>
            </section>

            {/* ── Footer ───────────────────────────────────────────── */}
            <footer className="eg-footer">
                <span>
                    &copy; <i>'ello</i>Govna 2026
                </span>
                <a
                    className="eg-footer-social"
                    href="https://www.linkedin.com/company/ellogovna/"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="'elloGovna on LinkedIn"
                >
                    <i className="bi bi-linkedin" />
                </a>
            </footer>

            <LoginModal />
            <RegisterModal />
        </>
    );
}
